// /hooks/useEnvelopeParticipants.ts
import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";

import {
	computeAddPermissions,
	hasDuplicateCpfOrEmail,
	isObserverType,
	isSignerOrWitnessType,
	normalizeCpf,
	normalizeEnvelopeStatus,
	normalizeParticipantType,
} from "../modals/tabs/rules";
import type { ISignatario } from "../types/types";
import { useToast } from "../../../shared/hooks";
import { SignatureServices } from "../../../shared/services/api/signature/SignatureServices";
import { PeopleService } from "../../../shared/services/api/people/PeopleService";
import { ToastContent } from "../../../shared/components";

interface UseParticipantsProps {
	envelopeId?: string;
	signers: ISignatario[];
	observers: ISignatario[];
	onChange: (nextSigners: ISignatario[], nextObservers: ISignatario[]) => void;
	envelopeStatus?: string;
}

export function useEnvelopeParticipants({ envelopeId, signers, observers, onChange, envelopeStatus }: UseParticipantsProps) {
	const toast = useToast();
	const [isLoadingDocument, setIsLoadingDocument] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const envSt = normalizeEnvelopeStatus(envelopeStatus);
	const perms = computeAddPermissions(envSt);

	const { safeSigners, safeObservers } = useMemo(() => {
		const all = [...(signers ?? []), ...(observers ?? [])];
		const s: ISignatario[] = [];
		const o: ISignatario[] = [];
		for (const p of all) {
			if (isObserverType(p.type)) o.push(p);
			else s.push(p);
		}
		return { safeSigners: s, safeObservers: o };
	}, [signers, observers]);

	const allPeople = useMemo(() => [...safeSigners, ...safeObservers], [safeSigners, safeObservers]);

	// ✅ NOVO: Schema de validação movido para fora do handleAddParticipant
	const addParticipantSchema = yup.object().shape({
		name: yup.string().trim().required("Informe o nome."),
		email: yup.string().trim().email("E-mail inválido.").required("Informe o e-mail."),
		document: yup
			.string()
			.transform((v) => normalizeCpf(v))
			.length(11, "CPF inválido.")
			.required("Informe o CPF."),
		type: yup
			.string()
			.transform((v) => (v === "observer" ? "observers" : v))
			.oneOf(["signer", "witness", "observers"])
			.required("Informe o tipo."),
	});

	const handleAddParticipant = async (values: any) => {
		if (!envelopeId) return;

		const valuesToValidate = { ...values, document: normalizeCpf(values?.document) };

		try {
			// ✅ NOVO: Validação do Yup antes do try/catch. Se falhar, o erro é retornado imediatamente.
			await addParticipantSchema.validate(valuesToValidate, { abortEarly: false });
		} catch (e: any) {
			return e; // Retorna o erro do Yup para o componente de UI
		}

		try {
			const { name, email, document } = valuesToValidate;
			const type: "signer" | "witness" | "observers" = normalizeParticipantType(valuesToValidate.type);

			if (isSignerOrWitnessType(type) && !perms.signer) {
				throw new Error("Assinantes/Testemunhas só podem ser incluídos quando o envelope estiver em Rascunho.");
			}
			if (isObserverType(type) && !perms.observers) {
				throw new Error("Observadores só podem ser incluídos quando o envelope estiver em Rascunho ou Em Progresso.");
			}

			const { dupCpf, dupEmail } = hasDuplicateCpfOrEmail(allPeople, document, email);
			if (dupCpf || dupEmail) {
				const msg =
					dupCpf && dupEmail
						? "Já existe um Signatário com este CPF e e-mail."
						: dupCpf
						? "Já existe um Signatário com este CPF."
						: "Já existe um Signatário com este e-mail.";
				// ✅ NOVO: Exibe o toast para o erro de duplicidade
				toast.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content={msg} />,
				});
				return new Error(msg);
			}

			setIsSubmitting(true);
			let res;
			if (isObserverType(type)) {
				res = await SignatureServices.createObservador(envelopeId, { name, email, document, type: "observers" });
				if (res instanceof Error) throw new Error("Erro ao incluir observador.");
				onChange(safeSigners, [...safeObservers, res]);
			} else {
				if (type === "witness") {
					throw new Error("Não é possível incluir testemunha por este método.");
				}
				res = await SignatureServices.createSignatario(envelopeId, { name, email, document, type: "signer" });
				if (res instanceof Error) throw new Error("Erro ao incluir signatário.");
				onChange([...safeSigners, res], safeObservers);
			}

			toast.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Sucesso" content="Signatário incluído." />,
			});
			return true;
		} catch (e: any) {
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content={"Erro ao incluir Signatário."} />,
			});
			return e;
		} finally {
			setIsSubmitting(false);
		}
	};

	// ... (restante do código do hook)

	const handleRemoveParticipant = async (p: ISignatario) => {
		// ... (código da função de remoção, que já está ok)
		try {
			if (!envelopeId || !p.id) {
				throw new Error("ID do envelope ou Signatário não fornecido.");
			}

			if (isObserverType(p.type)) {
				const res = await SignatureServices.deleteObservador(envelopeId, p.id);
				if (res instanceof Error) {
					throw new Error("Erro ao remover observador.");
				}
				onChange(
					safeSigners,
					safeObservers.filter((o) => o.id !== p.id)
				);
			} else {
				// 'signer' ou 'witness'
				const res = await SignatureServices.deleteSignatario(envelopeId, p.id);
				if (res instanceof Error) {
					throw new Error("Erro ao remover signatário.");
				}
				onChange(
					safeSigners.filter((s) => s.id !== p.id),
					safeObservers
				);
			}

			toast.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Removido" content="Signatário removido com sucesso." />,
			});
		} catch (e: any) {
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content={"Erro ao remover Signatário."} />,
			});
		}
	};

	const handleResendLink = async (p: ISignatario) => {
		// ... (código da função de reenvio, que já está ok)
		try {
			if (!envelopeId || !p.id) {
				throw new Error("ID do envelope ou participante não fornecido.");
			}

			if (isObserverType(p.type)) {
				const res = await SignatureServices.resendLinkToObservador(envelopeId, p.id);
				if (res instanceof Error) {
					throw new Error("Erro ao reenviar link para o observador.");
				}
			} else {
				const res = await SignatureServices.resendLinkToSignatario(envelopeId, p.id);
				if (res instanceof Error) {
					throw new Error("Erro ao reenviar link para o signatário.");
				}
			}
			toast.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Sucesso" content="Link de acesso reenviado." />,
			});
		} catch (e: any) {
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content={"Erro ao reenviar link."} />,
			});
		}
	};

	const handleCpfBlur = async (cpf: string) => {
		if (!cpf || normalizeCpf(cpf).length !== 11) return null;
		try {
			setIsLoadingDocument(true);
			const data: any = await PeopleService.getPersonDataByDocument(normalizeCpf(cpf));
			return data?.naturalPersonData?.name || data?.legalPersonData?.name || data?.name || null;
		} finally {
			setIsLoadingDocument(false);
		}
	};

	return {
		safeSigners,
		safeObservers,
		totalCountSignersOnly: safeSigners.length,
		inputsDisabled: perms.inputsDisabled,
		allowSignerWitness: perms.signer,
		allowObserver: perms.observers,
		defaultTypeForForm: perms.defaultType,
		isLoadingDocument,
		isSubmitting,
		handleAddParticipant,
		handleRemoveParticipant,
		handleResendLink,
		handleCpfBlur,
	};
}
